import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, MapPin, Star, ExternalLink, Building2, ShoppingBag, Factory, Train, Store } from 'lucide-react';

const TYPE_META = {
  plaza_comercial: { icon: ShoppingBag, label: 'Plaza comercial', color: 'text-blue-900 bg-blue-50' },
  strip_mall: { icon: ShoppingBag, label: 'Strip mall', color: 'text-blue-900 bg-blue-50' },
  mercado: { icon: Store, label: 'Mercado', color: 'text-amber-800 bg-amber-50' },
  parque_industrial: { icon: Factory, label: 'Parque industrial', color: 'text-slate-800 bg-slate-100' },
  edificio_oficinas: { icon: Building2, label: 'Edificio de oficinas', color: 'text-slate-800 bg-slate-100' },
  terminal_transporte: { icon: Train, label: 'Terminal transporte', color: 'text-purple-800 bg-purple-50' },
  local_independiente: { icon: Store, label: 'Local independiente', color: 'text-green-800 bg-green-50' },
  zona_hospitalaria: { icon: MapPin, label: 'Zona hospitalaria', color: 'text-red-800 bg-red-50' },
  zona_escolar: { icon: MapPin, label: 'Zona escolar', color: 'text-indigo-800 bg-indigo-50' },
  zona_turistica: { icon: MapPin, label: 'Zona turística', color: 'text-teal-800 bg-teal-50' },
  coordenadas: { icon: MapPin, label: 'Coordenadas', color: 'text-slate-700 bg-slate-100' },
  coordenadas_maps: { icon: MapPin, label: 'Punto en Maps', color: 'text-slate-700 bg-slate-100' },
  otro: { icon: MapPin, label: 'Otro', color: 'text-slate-600 bg-slate-100' },
};

export default function UniversalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(null);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true); setSelected(null);
    try {
      const r = await api.get('/places/resolve', { params: { q } });
      setResult(r.data);
      if (r.data.count === 0) toast.error('Sin coincidencias — prueba otra búsqueda');
    } catch { toast.error('Error en la búsqueda'); }
    finally { setLoading(false); }
  };

  const openPlace = async (c) => {
    if (!c.place_id) { setSelected(c); return; }
    setLoading(true);
    try {
      const r = await api.get(`/places/${c.place_id}`);
      setSelected(r.data);
    } catch { setSelected(c); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]">
      <div className="label-caps">Etapa 2 · Buscador Universal</div>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-2 mb-2">
        Busca cualquier ubicación
      </h1>
      <p className="text-slate-600 mb-6 max-w-2xl">
        Dirección, negocio, plaza, coordenadas, enlace de Google Maps o Place ID. El sistema detecta automáticamente el tipo y clasifica el lugar.
      </p>

      <form onSubmit={doSearch} className="flex gap-2 mb-6">
        <div className="relative flex-1 max-w-2xl">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="universal-search-input"
            placeholder="Busca una dirección, negocio, plaza o coloca coordenadas."
            className="pl-10 h-12 bg-white text-base"
          />
        </div>
        <Button type="submit" disabled={loading} data-testid="universal-search-btn" className="bg-blue-900 hover:bg-blue-800 h-12 px-6">
          {loading ? 'Buscando…' : 'Buscar'}
        </Button>
      </form>

      {result && (
        <div className="mb-4 text-xs text-slate-500">
          Tipo detectado: <strong className="text-slate-800">{result.input_type}</strong> · {result.count} resultado(s)
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Candidatos */}
        <div className="space-y-3">
          {(result?.candidates || []).map((c, i) => {
            const meta = TYPE_META[c.type_detected] || TYPE_META.otro;
            const Icon = meta.icon;
            const isSelected = selected?.place_id === c.place_id;
            return (
              <button
                key={i}
                onClick={() => openPlace(c)}
                data-testid={`place-candidate-${i}`}
                className={`w-full text-left bg-white border p-4 transition-colors ${isSelected ? 'border-blue-900 shadow-md' : 'border-slate-200 hover:border-blue-900'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 uppercase tracking-widest ${meta.color}`}>
                        <Icon size={12} className="inline mr-1" />{meta.label}
                      </span>
                    </div>
                    <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                    {c.address && <div className="text-xs text-slate-500 mt-0.5 truncate">{c.address}</div>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
                      {c.rating && <span className="flex items-center gap-1"><Star size={12} className="text-amber-500" fill="currentColor" />{c.rating.toFixed(1)}</span>}
                      {c.reviews != null && <span>{c.reviews.toLocaleString('es-MX')} reseñas</span>}
                      {c.lat && c.lon && <span className="text-slate-400">{c.lat.toFixed(4)}, {c.lon.toFixed(4)}</span>}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Detalle del lugar seleccionado (Modo Plaza) */}
        <div>
          {selected ? (
            <div className="bg-white border border-slate-200 sticky top-4" data-testid="place-detail-card">
              {selected.photo_url && (
                <img src={selected.photo_url} alt={selected.name} className="w-full h-56 object-cover" />
              )}
              <div className="p-5">
                {(() => { const m = TYPE_META[selected.type_detected] || TYPE_META.otro; const I = m.icon; return (
                  <span className={`text-xs font-semibold px-2 py-0.5 uppercase tracking-widest ${m.color}`}>
                    <I size={12} className="inline mr-1" />{m.label}
                  </span>
                ); })()}
                <h2 className="font-display text-2xl font-bold text-slate-900 mt-2">{selected.name}</h2>
                {selected.address && <div className="text-sm text-slate-600 mt-1">{selected.address}</div>}

                <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-slate-100">
                  <KPI label="Calificación" value={selected.rating ? `★ ${selected.rating.toFixed(1)}` : '—'} />
                  <KPI label="Reseñas" value={selected.reviews != null ? selected.reviews.toLocaleString('es-MX') : '—'} />
                  <KPI label="Estado" value={selected.business_status || '—'} />
                  <KPI label="Nivel de precio" value={selected.price_level || '—'} />
                </div>

                {selected.hours?.length > 0 && (
                  <div className="mt-4">
                    <div className="label-caps mb-2">Horarios</div>
                    <ul className="text-xs text-slate-700 space-y-1">
                      {selected.hours.map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-slate-100 space-y-1 text-xs text-slate-600">
                  {selected.phone && <div>📞 {selected.phone}</div>}
                  {selected.website && <a href={selected.website} target="_blank" rel="noreferrer" className="text-blue-900 hover:underline flex items-center gap-1"><ExternalLink size={11} />{selected.website}</a>}
                  {selected.lat && selected.lon && <div className="text-slate-400">{selected.lat.toFixed(6)}, {selected.lon.toFixed(6)}</div>}
                </div>

                <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500">
                  <strong className="text-slate-700">Fuente:</strong> Google Places (New) · <strong>Trazabilidad:</strong> Datos directos de Google. Los análisis derivados marcarán claramente su confiabilidad.
                </div>

                <Button className="w-full mt-5 bg-blue-900 hover:bg-blue-800" data-testid="analyze-place-btn"
                  disabled={!selected.place_id}
                  onClick={() => nav(`/search/place/${selected.place_id}`)}>
                  Abrir vista detallada + Isócronas
                </Button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-slate-300 p-12 text-center text-slate-400">
              Selecciona un resultado para ver la ficha completa
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const KPI = ({ label, value }) => (
  <div>
    <div className="label-caps">{label}</div>
    <div className="font-display text-xl font-bold text-slate-900 mt-1">{value}</div>
  </div>
);
